import { useEffect, useState } from 'react';

import { fetchAuthenticatedBlob } from '@/lib/api-client';
import type { SupportAttachment } from '@/types/bug-reports';

/**
 * The backend plan's admin-only media passthrough (Task 10) — `support-media/` is private and
 * never served from the public R2 base URL (ADR-0020), so it has to go through this API route.
 */
function previewPath(key: string): string {
  return `/admin/support/media/${encodeURIComponent(key)}`;
}

function formatSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A plain `<img src>`/`<video src>` pointed at this admin-gated route can never load: the
 * browser never attaches the Bearer header a media element's own request carries, and even
 * with the right host every request would 401. Fetches the bytes through the authenticated
 * client instead and renders them as a blob URL, revoked on unmount/attachment change.
 */
function useBlobUrl(key: string): { url: string | undefined; failed: boolean } {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    setUrl(undefined);
    setFailed(false);

    fetchAuthenticatedBlob(previewPath(key))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        // A 401/404/network failure must not become a permanently blank <img>/<video>
        // with zero indication anything went wrong — show it instead.
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [key]);

  return { url, failed };
}

function AttachmentItem({ attachment }: { attachment: SupportAttachment }): JSX.Element {
  const { url, failed } = useBlobUrl(attachment.key);

  return (
    <div className="flex flex-col gap-1">
      {failed ? (
        <div className="flex h-32 w-32 items-center justify-center rounded bg-muted text-center text-xs text-muted-foreground">
          Couldn&apos;t load attachment
        </div>
      ) : attachment.contentType.startsWith('image/') ? (
        <img src={url} alt="Attachment" className="h-32 w-32 rounded object-cover" />
      ) : (
        <video src={url} controls className="h-32 w-48 rounded" />
      )}
      {attachment.sizeBytes > 0 && (
        <span className="text-xs text-muted-foreground">{formatSize(attachment.sizeBytes)}</span>
      )}
    </div>
  );
}

export function AttachmentPreview({
  attachments,
}: {
  attachments: SupportAttachment[];
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-4">
      {attachments.map((attachment) => (
        <AttachmentItem key={attachment.key} attachment={attachment} />
      ))}
    </div>
  );
}
